/**
 * 🎯 Public Archive Manager - THE Single Source of Truth
 * 
 * This is the ONLY place where public memories are managed.
 * All components should use this for accessing public archive content.
 */

import React, { createContext, useContext, useState, useRef, useCallback } from 'react'
import { getWebRTCConfig } from '../config/signaling'
import { createIPFSProvider } from '../utils/ipfsYjsProvider.js'

// THE single shared document configuration
const PUBLIC_ARCHIVE_DOC_ID = 'etherith-public-archive'
const MEMORIES_ARRAY_KEY = 'publicArchive'

// Create context for the public archive
const PublicArchiveContext = createContext(null)

// Public Archive Provider Component
export function PublicArchiveProvider({ children }) {
  // ========================================
  // STATE - THE SINGLE SOURCE OF TRUTH
  // ========================================
  
  const [publicMemories, setPublicMemories] = useState([])
  const [isReady, setIsReady] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [connectionStatus, setConnectionStatus] = useState('disconnected')
  const [peerCount, setPeerCount] = useState(0)
  const [lastUpdateTime, setLastUpdateTime] = useState(null)
  const [errorCount, setErrorCount] = useState(0)
  const [lastError, setLastError] = useState(null)
  
  // Refs for Yjs objects
  const sharedDocumentRef = useRef(null)
  const memoriesArrayRef = useRef(null)
  const providersRef = useRef([])
  const ipfsProviderRef = useRef(null)

  // ========================================
  // CORE INITIALIZATION
  // ========================================
  
  /**
   * Initialize the public archive - ONLY call this once per app
   */
  const initializePublicArchive = useCallback(async () => {
    try {
      console.log('🚀 Initializing PUBLIC ARCHIVE (Single Source of Truth)')
      setConnectionStatus('connecting')
      
      // Load Yjs dependencies
      const { getYjsInstance } = await import('../utils/yjsLoader.js')
      const { Y, WebrtcProvider, IndexeddbPersistence } = await getYjsInstance()
      
      // Create THE shared document
      const sharedDoc = new Y.Doc()
      const memoriesArray = sharedDoc.getArray(MEMORIES_ARRAY_KEY)
      
      console.log('📄 Created shared document:', PUBLIC_ARCHIVE_DOC_ID)
      
      // Set up IndexedDB persistence
      const indexeddbProvider = new IndexeddbPersistence(PUBLIC_ARCHIVE_DOC_ID, sharedDoc)
      
      // Set up WebRTC provider with working servers
      const webrtcConfig = getWebRTCConfig()
      const webrtcProvider = new WebrtcProvider(PUBLIC_ARCHIVE_DOC_ID, sharedDoc, {
        ...webrtcConfig,
        connect: true,
        awareness: indexeddbProvider.awareness || null
      })
      
      // 🌐 Set up IPFS provider for document storage on Pinata
      console.log('🌐 Setting up IPFS provider for document storage...')
      const ipfsProvider = createIPFSProvider(PUBLIC_ARCHIVE_DOC_ID, sharedDoc, {
        autoSave: true,
        saveInterval: 30000, // Save every 30 seconds
        pinataEndpoint: '/api/ipfs/upload'
      })
      
      // Store references
      sharedDocumentRef.current = sharedDoc
      memoriesArrayRef.current = memoriesArray
      providersRef.current = [indexeddbProvider, webrtcProvider]
      ipfsProviderRef.current = ipfsProvider
      
      setIsReady(true)
      setConnectionStatus('connected')
      
      // Set up event listeners
      setupEventListeners(memoriesArray, webrtcProvider, ipfsProvider)
      
      // Load initial memories
      loadMemoriesFromDocument()
      
      console.log('✅ Public Archive initialized successfully with IPFS storage')
      
      return sharedDoc
      
    } catch (error) {
      console.error('❌ Failed to initialize public archive:', error)
      setConnectionStatus('error')
      setLastError(error.message)
      setErrorCount(prev => prev + 1)
      throw error
    }
  }, [])

  // ========================================
  // EVENT LISTENERS
  // ========================================
  
  /**
   * Set up document change listeners
   */
  const setupEventListeners = useCallback((memoriesArray, webrtcProvider, ipfsProvider) => {
    if (!memoriesArray) {
      console.warn('⚠️ Cannot set up listeners - memories array not ready')
      return
    }
    
    // Listen for changes to the memories array
    memoriesArray.observe((event) => {
      console.log('🔄 Public archive changed:', {
        added: event.changes.added.size,
        deleted: event.changes.deleted.size,
        total: memoriesArray.length
      })
      
      // Reload memories when document changes
      loadMemoriesFromDocument()
    })
    
    // Set up WebRTC peer monitoring
    if (webrtcProvider) {
      webrtcProvider.on('peers', (peers) => {
        const newPeerCount = Array.isArray(peers) ? peers.length : (peers.peers ? peers.peers.length : 0)
        console.log(`🌐 Public archive peers: ${newPeerCount}`)
        setPeerCount(newPeerCount)
        setIsConnected(newPeerCount > 0)
      })
      
      webrtcProvider.on('status', ({ status }) => {
        console.log(`📡 Public archive WebRTC status: ${status}`)
      })
    }
    
    // 🌐 Set up IPFS provider event listeners
    if (ipfsProvider) {
      // Listen for successful IPFS saves
      window.addEventListener('ipfs-yjs-ipfs-saved', (event) => {
        const { cid, timestamp } = event.detail
        console.log('✅ Document saved to IPFS:', { cid, timestamp })
        setLastUpdateTime(timestamp)
      })
      
      // Listen for IPFS load events
      window.addEventListener('ipfs-yjs-ipfs-loaded', (event) => {
        const { cid, size } = event.detail
        console.log('📥 Document loaded from IPFS:', { cid, size })
        // Refresh memories after IPFS load
        loadMemoriesFromDocument()
      })
      
      // Listen for IPFS save failures
      window.addEventListener('ipfs-yjs-ipfs-save-failed', (event) => {
        const error = event.detail
        console.error('❌ IPFS save failed:', error)
        setLastError(error.message || 'IPFS save failed')
        setErrorCount(prev => prev + 1)
      })
    }
    
    console.log('👂 Event listeners set up for public archive (including IPFS)')
  }, [])

  // ========================================
  // MEMORY OPERATIONS
  // ========================================
  
  /**
   * Load memories from the shared document
   */
  const loadMemoriesFromDocument = useCallback(() => {
    const memoriesArray = memoriesArrayRef.current
    
    if (!memoriesArray) {
      console.warn('⚠️ Cannot load memories - array not ready')
      return
    }
    
    try {
      const memories = memoriesArray.toArray()
      
      console.log('🌐 Loaded public memories from shared document:', {
        total: memories.length,
        withCID: memories.filter(m => m.cid).length,
        withoutCID: memories.filter(m => !m.cid).length,
        memoryIds: memories.map(m => m.id).slice(0, 3)
      })
      
      setPublicMemories(memories)
      setLastUpdateTime(Date.now())
      
    } catch (error) {
      console.error('❌ Failed to load memories:', error)
      setLastError(error.message)
      setErrorCount(prev => prev + 1)
    }
  }, [])
  
  /**
   * Add a memory to the shared document
   */
  const addMemoryToArchive = useCallback((memory) => {
    const memoriesArray = memoriesArrayRef.current
    
    if (!memoriesArray) {
      throw new Error('Public archive not ready')
    }
    
    try {
      // Add memory to shared document
      memoriesArray.push([memory])
      
      console.log('✅ Added memory to public archive:', {
        id: memory.id,
        fileName: memory.fileName,
        cid: memory.cid
      })
      
      return true
      
    } catch (error) {
      console.error('❌ Failed to add memory to archive:', error)
      throw error
    }
  }, [])
  
  /**
   * Remove a memory from the shared document
   */
  const removeMemoryFromArchive = useCallback((memoryId) => {
    const memoriesArray = memoriesArrayRef.current
    
    if (!memoriesArray) {
      throw new Error('Public archive not ready')
    }
    
    try {
      const memories = memoriesArray.toArray()
      const index = memories.findIndex(m => m.id === memoryId)
      
      if (index !== -1) {
        memoriesArray.delete(index, 1)
        console.log('✅ Removed memory from public archive:', memoryId)
        return true
      }
      
      return false
      
    } catch (error) {
      console.error('❌ Failed to remove memory from archive:', error)
      throw error
    }
  }, [])

  // ========================================
  // IPFS DOCUMENT OPERATIONS
  // ========================================
  
  /**
   * Force save the document to IPFS immediately
   */
  const saveDocumentToIPFS = useCallback(async () => {
    const ipfsProvider = ipfsProviderRef.current
    if (!ipfsProvider) {
      throw new Error('IPFS provider not initialized')
    }
    
    try {
      console.log('💾 Force saving document to IPFS...')
      const result = await ipfsProvider.forceSave()
      console.log('✅ Document force saved to IPFS:', result.cid)
      return result
    } catch (error) {
      console.error('❌ Failed to save document to IPFS:', error)
      throw error
    }
  }, [])
  
  /**
   * Get the current IPFS CID of the document
   */
  const getDocumentCID = useCallback(() => {
    const ipfsProvider = ipfsProviderRef.current
    return ipfsProvider ? ipfsProvider.getCurrentCID() : null
  }, [])
  
  /**
   * Get IPFS provider status
   */
  const getIPFSStatus = useCallback(() => {
    const ipfsProvider = ipfsProviderRef.current
    return ipfsProvider ? ipfsProvider.getStatus() : {
      isConnected: false,
      currentCID: null,
      lastSaveTime: null,
      documentId: null
    }
  }, [])
  
  /**
   * Clean up all providers and resources
   */
  const cleanup = useCallback(() => {
    console.log('🧹 Cleaning up public archive and IPFS provider...')
    
    // Cleanup IPFS provider
    if (ipfsProviderRef.current) {
      ipfsProviderRef.current.disconnect()
      ipfsProviderRef.current = null
    }
    
    // Cleanup other providers
    providersRef.current.forEach(provider => {
      if (provider.destroy) {
        provider.destroy()
      } else if (provider.disconnect) {
        provider.disconnect()
      }
    })
    
    // Reset refs
    sharedDocumentRef.current = null
    memoriesArrayRef.current = null
    providersRef.current = []
    
    // Reset state
    setPublicMemories([])
    setIsReady(false)
    setIsConnected(false)
    setConnectionStatus('disconnected')
    setPeerCount(0)
    setLastUpdateTime(null)
    setErrorCount(0)
    setLastError(null)
    
    console.log('✅ Public archive cleanup completed')
  }, [])

  // Context value
  const value = {
    // State
    publicMemories,
    isReady,
    isConnected,
    connectionStatus,
    peerCount,
    lastUpdateTime,
    errorCount,
    lastError,
    
    // Actions
    initializePublicArchive,
    addMemoryToArchive,
    removeMemoryFromArchive,
    loadMemoriesFromDocument,
    cleanup,
    
    // IPFS Actions
    saveDocumentToIPFS,
    getDocumentCID,
    getIPFSStatus
  }

  return (
    <PublicArchiveContext.Provider value={value}>
      {children}
    </PublicArchiveContext.Provider>
  )
}

// Hook to use the public archive manager
export function usePublicArchiveManager() {
  const context = useContext(PublicArchiveContext)
  if (!context) {
    throw new Error('usePublicArchiveManager must be used within PublicArchiveProvider')
  }
  return context
}

// ========================================
// CONVENIENCE HOOKS
// ========================================

/**
 * Hook to get public memories (read-only)
 */
export const usePublicMemories = () => {
  const { publicMemories } = usePublicArchiveManager()
  return publicMemories
}

/**
 * Hook to get archive status including IPFS
 */
export const useArchiveStatus = () => {
  const { isReady, isConnected, connectionStatus, peerCount, publicMemories, getIPFSStatus, getDocumentCID } = usePublicArchiveManager()
  const ipfsStatus = getIPFSStatus()
  const documentCID = getDocumentCID()
  
  return {
    isReady,
    isConnected,
    connectionStatus,
    peerCount,
    memoriesCount: publicMemories.length,
    ipfs: ipfsStatus,
    documentCID
  }
}

/**
 * Hook to get IPFS document operations
 */
export const useIPFSDocument = () => {
  const { saveDocumentToIPFS, getDocumentCID, getIPFSStatus } = usePublicArchiveManager()
  return {
    saveDocumentToIPFS,
    getDocumentCID,
    getIPFSStatus
  }
}